/* =============================================================
   paneClip.js — freeze-pane geometry for floating objects
   (charts / images) that straddle a frozen row/column boundary.

   Excel splits the sheet into up to four panes when rows and/or
   columns are frozen:

       corner | frozen-row band
       -------+----------------
       frozen | body
       col    |
       band   |

   A chart that crosses a freeze line is shown in PIECES: the part in
   the frozen rows stays pinned to the top while the rest scrolls, the
   part in the frozen columns stays pinned to the left, etc.  We render
   the same chart once per pane it overlaps, each copy shifted so it
   stays pinned in the frozen axis and clipped to that pane's rectangle.

   All inputs are in CONTENT pixels (pre-zoom):
     L,T,W,H            chart box (left, top, width, height)
     freezeX, freezeY   freeze lines (left of body / top of body)
     scrollX, scrollY   current scroll offset of the grid

   Returns { offsetX, offsetY, clipPath } for the requested pane, or
   null when the chart has no portion inside that pane (so the caller
   can skip rendering that copy).
   ============================================================= */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function computePane(pane, L, T, W, H, freezeX, freezeY, scrollX, scrollY) {
  // Body shows content at/after these lines (frozen bands scroll under it).
  const bodyLeft = freezeX + scrollX;
  const bodyTop = freezeY + scrollY;

  const overlapsFrozenRows = T < freezeY;       // chart reaches into frozen rows
  const overlapsFrozenCols = L < freezeX;       // chart reaches into frozen cols

  let offsetX = 0;   // content px to shift the box to pin it in an axis
  let offsetY = 0;
  let insL = 0, insT = 0, insR = 0, insB = 0;   // clip insets in box-local px

  switch (pane) {
    case "body":
      // Keep the part right of and below the freeze lines.
      insL = clamp(bodyLeft - L, 0, W);
      insT = clamp(bodyTop - T, 0, H);
      if (insL >= W || insT >= H) return null;  // nothing left
      break;

    case "row": // frozen rows, body columns — pinned vertically
      if (!overlapsFrozenRows) return null;
      offsetY = scrollY;                          // pin to top
      insB = clamp(H - (freezeY - T), 0, H);      // keep above freeze line
      insL = clamp(bodyLeft - L, 0, W);           // body columns only
      if (insB >= H || insL >= W) return null;
      break;

    case "col": // frozen cols, body rows — pinned horizontally
      if (!overlapsFrozenCols) return null;
      offsetX = scrollX;                          // pin to left
      insR = clamp(W - (freezeX - L), 0, W);      // keep left of freeze line
      insT = clamp(bodyTop - T, 0, H);            // body rows only
      if (insR >= W || insT >= H) return null;
      break;

    case "corner": // frozen rows AND cols — pinned both axes
      if (!overlapsFrozenRows || !overlapsFrozenCols) return null;
      offsetX = scrollX;
      offsetY = scrollY;
      insR = clamp(W - (freezeX - L), 0, W);
      insB = clamp(H - (freezeY - T), 0, H);
      if (insR >= W || insB >= H) return null;
      break;

    default:
      return null;
  }

  const hasClip = insL || insT || insR || insB;
  const clipPath = hasClip ? `inset(${insT}px ${insR}px ${insB}px ${insL}px)` : undefined;
  return { offsetX, offsetY, clipPath };
}

// Pane render order / stacking. Body sits with the cells; the pinned
// bands float above the frozen cells so the chart shows over them.
export const PANES = ["body", "col", "row", "corner"];
export const PANE_Z = { body: 10, col: 16, row: 16, corner: 17 };
